import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  Slider,
  Switch,
  Select,
  MenuItem,
  Chip,
  Alert,
  Tooltip,
  CircularProgress,
  IconButton,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Checkbox,
  ListSubheader
} from '@mui/material';
import {
  Close as CloseIcon,
  AdminPanelSettings as AdminIcon,
  Storefront as StorefrontIcon,
  Lock as LockIcon,
  InfoOutlined as InfoIcon,
  ColorLens as ColorLensIcon,
  Layers as LayersIcon,
  PersonAddAlt1 as PersonAddIcon,
  Check as CheckIcon,
  Clear as ClearIcon,
  FolderOutlined as FolderIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { Role, ModuleGroup, OperatorContext, TargetInterface } from '../types';
import { notify } from '../../../notifications';
import { PERMISSION_SCOPES_CONFIG, BADGE_PRESETS, getHierarchyInfo, HIERARCHY_STEPS } from '../scopesConfig';
import {
  getPermissionSubgroup,
  getAnchorLabel,
  MASTER_ANCHOR_LABELS,
  DEFAULT_MASTER_ANCHOR_LABEL
} from '../subgroupsConfig';
import { groupModulesByMenu, filterModulesForInterface } from '../menuGroupsConfig';
import { HexColorPicker } from 'react-colorful';
import {
  PROFILE_START_PAGE,
  getStartPageOptions,
  findStartPage,
  getMissingStartPagePerms
} from '../../../../config/startPages';

interface RoleMatrixEditorProps {
  open: boolean;
  role: Role | null;
  modules: ModuleGroup[];
  availableRoles?: Role[];
  operator: OperatorContext;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
}

// ─── Design constants ─────────────────────────────────────────────────────────
const C = {
  border: '#e2e8f0',
  bg: '#f8fafc',
  bgWhite: '#ffffff',
  text: '#0f172a',
  textMuted: '#64748b',
  textLight: '#94a3b8',
  accent: '#2563eb',
  accentBg: '#eff6ff',
  green: '#059669',
  greenBg: '#ecfdf5',
  danger: '#dc2626',
  dangerBg: '#fef2f2',
  amber: '#d97706',
};

// ─── Section header utility ───────────────────────────────────────────────────
const Section: React.FC<{ label: string; children: React.ReactNode; mt?: number }> = ({ label, children, mt = 0 }) => (
  <Box sx={{ mt }}>
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 700,
        fontSize: '0.7rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: C.textLight,
        mb: 1
      }}
    >
      {label}
    </Typography>
    {children}
  </Box>
);

// ─── Reusable styles ──────────────────────────────────────────────────────────
const textFieldSx = {
  '& .MuiInputBase-root': { fontSize: '0.85rem', borderRadius: '6px' },
  '& .MuiInputLabel-root': { fontSize: '0.85rem' },
  '& .MuiFormHelperText-root': { fontSize: '0.72rem' }
};

const primaryBtnSx = {
  backgroundColor: '#0f172a',
  color: '#ffffff',
  fontWeight: 700,
  fontSize: '0.82rem',
  textTransform: 'none' as const,
  borderRadius: '6px',
  px: 2.5,
  py: 0.85,
  '&:hover': { backgroundColor: '#1e293b' },
  '&.Mui-disabled': { backgroundColor: '#e2e8f0', color: '#94a3b8' }
};

// ─── Helper: hex → luminance → auto text/border ───────────────────────────────
function hexToRgb(hex: string): [number, number, number] | null {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return null;
  return [
    parseInt(clean.slice(0, 2), 16),
    parseInt(clean.slice(2, 4), 16),
    parseInt(clean.slice(4, 6), 16)
  ];
}

function relativeLuminance(r: number, g: number, b: number): number {
  const s = [r, g, b].map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * s[0] + 0.7152 * s[1] + 0.0722 * s[2];
}

function darken(hex: string, amount = 0.35): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const [r, g, b] = rgb.map(v => Math.max(0, Math.round(v * (1 - amount))));
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

function deriveTextAndBorder(bgHex: string): { text: string; border: string } {
  const rgb = hexToRgb(bgHex);
  if (!rgb) return { text: '#1e293b', border: '#e2e8f0' };
  const lum = relativeLuminance(...rgb);
  const text = lum > 0.35 ? darken(bgHex, 0.55) : lum > 0.15 ? darken(bgHex, 0.4) : '#ffffff';
  const border = lum > 0.5 ? darken(bgHex, 0.2) : darken(bgHex, 0.08);
  return { text, border };
}

// ─── BadgeColorPicker component ───────────────────────────────────────────────
interface BadgeColorPickerProps {
  badge: { bg: string; text: string; border: string };
  previewLabel: string;
  onChange: (badge: { bg: string; text: string; border: string }) => void;
}

const BadgeColorPicker: React.FC<BadgeColorPickerProps> = ({ badge, previewLabel, onChange }) => {
  const [hexInput, setHexInput] = useState(badge.bg);

  // Sync external badge changes to local hex input
  useEffect(() => { setHexInput(badge.bg); }, [badge.bg]);

  const handlePickerChange = (newHex: string) => {
    setHexInput(newHex);
    const { text, border } = deriveTextAndBorder(newHex);
    onChange({ bg: newHex, text, border });
  };

  const handleHexInputChange = (val: string) => {
    setHexInput(val);
    const clean = val.startsWith('#') ? val : '#' + val;
    if (/^#[0-9a-f]{6}$/i.test(clean)) {
      const { text, border } = deriveTextAndBorder(clean);
      onChange({ bg: clean, text, border });
    }
  };

  return (
    <Section label="Оформление бейджа">
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap' }}>
        {/* Left: color picker */}
        <Box sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              '& .react-colorful': {
                width: '200px',
                height: '160px',
                borderRadius: '8px',
                border: `1px solid ${C.border}`,
                overflow: 'hidden'
              },
              '& .react-colorful__saturation': { borderRadius: '6px 6px 0 0', flexGrow: 1 },
              '& .react-colorful__hue': {
                height: '12px',
                borderRadius: '0 0 6px 6px',
                margin: '0'
              },
              '& .react-colorful__pointer': {
                width: '14px',
                height: '14px',
                borderWidth: '2px'
              }
            }}
          >
            <HexColorPicker color={badge.bg} onChange={handlePickerChange} />
          </Box>
          {/* Hex input */}
          <Box
            sx={{
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: `1px solid ${C.border}`,
              borderRadius: '6px',
              px: 1.25,
              py: 0.5,
              backgroundColor: C.bgWhite,
              width: 200
            }}
          >
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: '3px',
                backgroundColor: badge.bg,
                border: `1px solid ${C.border}`,
                flexShrink: 0
              }}
            />
            <input
              value={hexInput}
              onChange={e => handleHexInputChange(e.target.value)}
              style={{
                border: 'none',
                outline: 'none',
                fontSize: '0.82rem',
                fontFamily: 'monospace',
                fontWeight: 600,
                color: C.text,
                background: 'transparent',
                width: '100%',
                textTransform: 'uppercase'
              }}
              placeholder="#000000"
              maxLength={7}
            />
          </Box>
        </Box>

        {/* Right: preview + presets */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Live preview */}
          <Box
            sx={{
              mb: 1.5,
              p: 1.5,
              backgroundColor: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', color: C.textMuted }}>Образец:</Typography>
            <Chip
              label={previewLabel}
              size="small"
              sx={{
                backgroundColor: badge.bg,
                color: badge.text,
                border: `1px solid ${badge.border}`,
                fontWeight: 700,
                fontSize: '0.78rem'
              }}
            />
          </Box>

          {/* Preset swatches */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(8, 1fr)',
              gap: 0.75
            }}
          >
            {BADGE_PRESETS.map((p, idx) => {
              const isActive = badge.bg === p.bg && badge.text === p.text;
              return (
                <Tooltip key={idx} title={p.name} placement="top">
                  <Box
                    onClick={() => {
                      setHexInput(p.bg);
                      onChange({ bg: p.bg, text: p.text, border: p.border });
                    }}
                    sx={{
                      height: 24,
                      borderRadius: '4px',
                      backgroundColor: p.bg,
                      border: `2px solid ${isActive ? p.text : p.border}`,
                      cursor: 'pointer',
                      transition: 'all 0.1s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: isActive ? `0 0 0 1px ${p.text}60` : 'none',
                      '&:hover': { border: `2px solid ${p.text}`, transform: 'scale(1.1)' }
                    }}
                  >
                    <Typography sx={{ fontSize: '0.5rem', fontWeight: 700, color: p.text, lineHeight: 1 }}>
                      Aa
                    </Typography>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Section>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const isSpecificRolesScope = (val: string | undefined): boolean => {
  if (!val) return false;
  return val === 'specific_roles' || val.startsWith('specific_roles:') || val.startsWith('[');
};

const parseSpecificRoles = (val: string | undefined): string[] => {
  if (!val) return [];
  if (val.startsWith('specific_roles:')) {
    return val
      .replace(/^specific_roles:/, '')
      .split(',')
      .map(s => s.trim().toLowerCase())
      .filter(Boolean);
  }
  if (val.startsWith('[')) {
    try {
      const arr = JSON.parse(val);
      if (Array.isArray(arr)) {
        return arr.map(s => String(s).trim().toLowerCase()).filter(Boolean);
      }
    } catch { }
  }
  return [];
};

// ─── Боковая навигация диалога: разделы и якоря ───────────────────────────────
interface NavCard {
  /** id DOM-элемента карточки */
  id: string;
  /** id якоря в боковом меню, которому принадлежит карточка */
  anchorId: string;
}

interface NavAnchorItem {
  id: string;
  label: string;
}

const PARAM_ANCHORS: NavAnchorItem[] = [
  { id: 'rm-params-basic', label: 'Основные данные' },
  { id: 'rm-params-rank', label: 'Иерархия доступа' },
  { id: 'rm-params-interface', label: 'Целевой интерфейс' },
  { id: 'rm-params-start', label: 'Стартовая страница' },
  { id: 'rm-params-badge', label: 'Цвет бейджа' }
];

const PARAM_CARDS: NavCard[] = PARAM_ANCHORS.map(a => ({ id: a.id, anchorId: a.id }));

type ModulePermission = ModuleGroup['permissions'][number];

/** Права раздела «Документы», зависящие от права просмотра документов (documents.invoices.view) */
const DOCUMENTS_VIEW_DEPENDENTS: string[] = [
  'documents.invoices.view_doc_amount',
  'documents.invoices.view_item_amounts',
  'documents.invoices.view_marks',
  'documents.invoices.view_series',
  'documents.invoices.view_history',
  'documents.invoices.view_audit',
  'documents.invoices.export',
  'documents.invoices.export_marks',
  'documents.invoices.retry',
  'documents.invoices.hide',
  'documents.invoices.reject',
  'documents.invoices.delete_draft',
  'documents.invoices.export_1c',
  'documents.invoices.warnings',
  'documents.invoices.analytics',
  'documents.invoices.settings',
  // Коды маркировки берутся из накладных в зоне просмотра документов
  'documents.marking.view'
];

/** Права журнала действий, зависящие от права просмотра журнала (audit.view) */
const AUDIT_VIEW_DEPENDENTS: string[] = ['audit.details.view', 'audit.export', 'audit.cleanup'];

/** Права электронного журнала (журнала сроков), работающие только внутри зоны просмотра (journal.view) */
const JOURNAL_VIEW_DEPENDENTS: string[] = ['journal.analytics.view', 'journal.export'];

/** Права, которые можно включить только рангу 100: код → подсказка у бейджа «Только ранг 100» */
const RANK_100_ONLY_PERMISSIONS: Record<string, string> = {
  'users.delete': 'Удаление пользователей доступно только рангу 100',
  'audit.cleanup': 'Очистка журнала действий доступна только рангу 100'
};

interface ModuleLayout {
  masterPerm?: ModulePermission;
  subgroups: { name: string; permissions: ModulePermission[]; cardId: string }[];
  masterCardId: string;
  anchors: NavAnchorItem[];
  cards: NavCard[];
}

/** Главный переключатель модуля: `<module>.menu` (для create_account - `users.create`) */
const isMasterPermOf = (moduleId: string, code: string): boolean =>
  code.endsWith('.menu') || (moduleId === 'create_account' && code === 'users.create');

function buildModuleLayout(m: ModuleGroup | undefined): ModuleLayout | null {
  if (!m || !m.permissions || m.permissions.length === 0) return null;

  const masterPerm = m.permissions.find(p => isMasterPermOf(m.id, p.code));
  const otherPerms = m.permissions.filter(p => !isMasterPermOf(m.id, p.code));

  // Группировка прав по подразделам с сохранением порядка появления
  const subgroupMap = new Map<string, ModulePermission[]>();
  for (const perm of otherPerms) {
    const groupName = getPermissionSubgroup(perm.code) || 'Общие разрешения';
    if (!subgroupMap.has(groupName)) subgroupMap.set(groupName, []);
    subgroupMap.get(groupName)!.push(perm);
  }

  const masterCardId = `rm-${m.id}-master`;
  const anchors: NavAnchorItem[] = [];
  const cards: NavCard[] = [];
  const anchorByLabel = new Map<string, string>();

  if (masterPerm) {
    anchors.push({ id: masterCardId, label: MASTER_ANCHOR_LABELS[m.id] || DEFAULT_MASTER_ANCHOR_LABEL });
    cards.push({ id: masterCardId, anchorId: masterCardId });
  }

  const subgroups: ModuleLayout['subgroups'] = [];
  let idx = 0;
  subgroupMap.forEach((permissions, name) => {
    const cardId = `rm-${m.id}-g${idx++}`;
    const label = getAnchorLabel(name);
    let anchorId = anchorByLabel.get(label);
    if (!anchorId) {
      anchorId = cardId;
      anchorByLabel.set(label, cardId);
      anchors.push({ id: cardId, label });
    }
    cards.push({ id: cardId, anchorId });
    subgroups.push({ name, permissions, cardId });
  });

  return { masterPerm, subgroups, masterCardId, anchors, cards };
}

// ─── Элементы бокового меню ───────────────────────────────────────────────────
const NavSectionHeader: React.FC<{ label: string; open: boolean; badge?: number; onClick: () => void }> = ({
  label,
  open,
  badge,
  onClick
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: 1.5,
      py: 1.1,
      cursor: 'pointer',
      borderBottom: `1px solid ${C.border}`,
      backgroundColor: open ? C.bgWhite : C.bg,
      '&:hover': { backgroundColor: C.bg }
    }}
  >
    <ExpandMoreIcon
      sx={{
        fontSize: 18,
        color: open ? C.accent : C.textLight,
        transform: open ? 'none' : 'rotate(-90deg)',
        transition: 'transform 0.15s'
      }}
    />
    <Typography
      sx={{
        flex: 1,
        fontSize: '0.72rem',
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: open ? C.text : C.textMuted
      }}
    >
      {label}
    </Typography>
    {badge !== undefined && badge > 0 && (
      <Box
        sx={{
          minWidth: 20,
          height: 18,
          borderRadius: '9px',
          backgroundColor: C.accent,
          color: '#fff',
          fontSize: '0.65rem',
          fontWeight: 700,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 0.5
        }}
      >
        {badge}
      </Box>
    )}
  </Box>
);

const NavAnchor: React.FC<{ label: string; active: boolean; onClick: () => void }> = ({ label, active, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      ml: 2.5,
      pl: 1.5,
      pr: 1,
      py: 0.6,
      cursor: 'pointer',
      borderLeft: `2px solid ${active ? C.accent : C.border}`,
      fontSize: '0.78rem',
      fontWeight: active ? 650 : 500,
      color: active ? C.accent : C.textMuted,
      lineHeight: 1.35,
      '&:hover': { color: active ? C.accent : C.text }
    }}
  >
    {label}
  </Box>
);

export const RoleMatrixEditor: React.FC<RoleMatrixEditorProps> = ({
  open,
  role,
  modules,
  availableRoles = [],
  operator,
  onClose,
  onSave
}) => {
  const isEditing = Boolean(role);

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [level, setLevel] = useState<number>(10);
  const [targetInterface, setTargetInterface] = useState<TargetInterface>('dashboard');
  const [startPage, setStartPage] = useState<string>(PROFILE_START_PAGE);
  const [badge, setBadge] = useState({ bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' });
  const [permissions, setPermissions] = useState<Record<string, string>>({});

  const [mainTab, setMainTab] = useState<0 | 1>(0);
  const [selectedModuleTab, setSelectedModuleTab] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const prevOpenRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollLockUntilRef = useRef(0);
  const [activeAnchor, setActiveAnchor] = useState<string>(PARAM_ANCHORS[0].id);
  const [pendingScroll, setPendingScroll] = useState<{ id: string | null; smooth: boolean } | null>(null);

  const maxAllowedLevel = useMemo(() => {
    if (operator.isSuperAdmin) return 100;
    return Math.max(10, operator.maxCreatableLevel);
  }, [operator]);

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (role) {
      setName(role.name || '');
      setSlug(role.slug || '');
      setDescription(role.description || '');
      setLevel(Math.max(10, Math.min(100, Math.round(Number(role.level || 10) / 10) * 10)));
      const roleInterface = role.targetInterface === 'admin' ? 'admin' : 'dashboard';
      setTargetInterface(roleInterface);
      setStartPage(findStartPage(roleInterface, role.startPage) ? (role.startPage as string) : PROFILE_START_PAGE);
      setBadge(role.badge || { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' });
      setPermissions({ ...(role.permissions || {}) });
    } else {
      setName('');
      setSlug('');
      setDescription('');
      setLevel(Math.min(20, maxAllowedLevel));
      setTargetInterface('dashboard');
      setStartPage(PROFILE_START_PAGE);
      setBadge({ bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' });
      setPermissions({});
    }

    if (!wasOpen && open) {
      setMainTab(0);
      setSelectedModuleTab(0);
      setActiveAnchor(PARAM_ANCHORS[0].id);
      setPendingScroll({ id: null, smooth: false });
    }
    setErrorMessage(null);
    setIsSubmitting(false);
  }, [role, open, maxAllowedLevel]);

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setName(val);
    if (!isEditing && !slug) {
      setSlug(
        val
          .toLowerCase()
          .replace(/[^a-z0-9а-яё]/gi, '_')
          .replace(/_+/g, '_')
          .slice(0, 30)
      );
    }
  };

  const hierarchyInfo = useMemo(() => getHierarchyInfo(level), [level]);

  // ─── Стартовая страница: набор вариантов зависит от интерфейса и включенных прав ───
  const hasMatrixPerm = useCallback((code: string) => {
    const val = permissions[code];
    return Boolean(val && val !== 'none' && val !== 'false');
  }, [permissions]);

  const startPageOptions = useMemo(() => getStartPageOptions(targetInterface), [targetInterface]);

  const startPageGroups = useMemo(() => {
    const groups: { name: string; items: { key: string; label: string }[] }[] = [];
    startPageOptions.forEach(opt => {
      let g = groups.find(x => x.name === opt.group);
      if (!g) {
        g = { name: opt.group, items: [] };
        groups.push(g);
      }
      // В списке только разделы, доступные роли по включенным правам матрицы
      if (getMissingStartPagePerms(opt, hasMatrixPerm).length > 0) return;
      g.items.push({ key: opt.key, label: opt.label });
    });
    return groups.filter(g => g.items.length > 0);
  }, [startPageOptions, hasMatrixPerm]);

  // Если выбранная страница стала недоступна (сменили интерфейс / отключили права) - возвращаем профиль
  useEffect(() => {
    if (startPage === PROFILE_START_PAGE) return;
    const opt = findStartPage(targetInterface, startPage);
    if (!opt || getMissingStartPagePerms(opt, hasMatrixPerm).length > 0) {
      setStartPage(PROFILE_START_PAGE);
    }
  }, [startPage, targetInterface, hasMatrixPerm]);

  const totalActivePerms = useMemo(
    () => Object.values(permissions).filter(v => v && v !== 'none' && v !== 'false').length,
    [permissions]
  );

  // У /admin и /dashboard разные матрицы прав и стартовые страницы: при смене интерфейса начинаем с чистого листа
  const handleInterfaceChange = (next: TargetInterface) => {
    if (next === targetInterface) return;
    setTargetInterface(next);
    setStartPage(PROFILE_START_PAGE);
    setPermissions({});
    setSelectedModuleTab(0);
  };

  const handleTogglePermission = (code: string, checked: boolean, defaultScope = 'true') => {
    setPermissions(prev => {
      const next = { ...prev };
      if (!checked) {
        delete next[code];
      } else {
        next[code] = defaultScope;
      }
      return next;
    });
  };

  const handleScopeChange = (code: string, newScope: string) => {
    setPermissions(prev => {
      const next = { ...prev };
      if (newScope === 'none' || newScope === 'false') {
        delete next[code];
      } else {
        next[code] = newScope;
      }
      return next;
    });
  };

  const handleSaveClick = async () => {
    if (!name.trim()) {
      setErrorMessage('Введите название роли');
      return;
    }
    if (level > maxAllowedLevel && !operator.isSuperAdmin) {
      setErrorMessage(`Ранг (${level}) превышает допустимый максимум (${maxAllowedLevel})`);
      return;
    }
    setErrorMessage(null);
    setIsSubmitting(true);
    try {
      await onSave({
        name: name.trim(),
        slug: slug.trim() || undefined,
        description: description.trim() || undefined,
        level,
        targetInterface,
        startPage,
        badge,
        permissions
      });
      notify.success('Изменения успешно сохранены');
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || err.message || 'Ошибка сохранения');
    } finally {
      setIsSubmitting(false);
    }
  };

  // У /admin и /dashboard разные матрицы: модули фильтруются по выбранному интерфейсу
  const interfaceModules = useMemo(() => filterModulesForInterface(modules, targetInterface), [modules, targetInterface]);
  const currentModule = interfaceModules[selectedModuleTab] || interfaceModules[0];
  const currentLayout = useMemo(() => buildModuleLayout(currentModule), [currentModule]);
  const groupedModules = useMemo(() => groupModulesByMenu(interfaceModules, targetInterface), [interfaceModules, targetInterface]);
  // Зависимость от права, которого нет в матрице этого интерфейса, строку не блокирует: включить его оператор не сможет
  const interfaceCodes = useMemo(
    () => new Set(interfaceModules.reduce<string[]>((codes, m) => codes.concat(m.permissions.map(p => p.code)), [])),
    [interfaceModules]
  );

  // Карточки текущего раздела (для подсветки активного якоря при прокрутке)
  const currentCards: NavCard[] = mainTab === 0 ? PARAM_CARDS : (currentLayout ? currentLayout.cards : []);

  /** Переход в раздел «Параметры» (tab=0) или в модуль матрицы (tab=1), опционально - к якорю */
  const goTo = (tab: 0 | 1, moduleIdx?: number, anchorId?: string) => {
    let idx = selectedModuleTab;
    if (tab === 1 && moduleIdx !== undefined) idx = moduleIdx;
    const switching = tab !== mainTab || idx !== selectedModuleTab;
    setMainTab(tab);
    if (tab === 1) setSelectedModuleTab(idx);

    const firstAnchor = tab === 0
      ? PARAM_ANCHORS[0].id
      : buildModuleLayout(interfaceModules[idx])?.anchors[0]?.id || '';
    const target = anchorId || null;
    setActiveAnchor(anchorId || firstAnchor);
    // На время прокрутки по клику подсветку по скроллу не пересчитываем
    scrollLockUntilRef.current = Date.now() + 900;
    setPendingScroll({ id: target, smooth: !switching && Boolean(target) });
  };

  // Прокрутка после отрисовки нового раздела
  useEffect(() => {
    if (!pendingScroll) return;
    const container = scrollRef.current;
    if (pendingScroll.id) {
      document.getElementById(pendingScroll.id)?.scrollIntoView({
        behavior: pendingScroll.smooth ? 'smooth' : 'auto',
        block: 'start'
      });
    } else if (container) {
      container.scrollTop = 0;
    }
    setPendingScroll(null);
  }, [pendingScroll, mainTab, selectedModuleTab]);

  // Подсветка активного якоря по положению прокрутки
  const handleContentScroll = () => {
    const container = scrollRef.current;
    if (!container || currentCards.length === 0) return;
    if (Date.now() < scrollLockUntilRef.current) return;
    const top = container.getBoundingClientRect().top;
    let current = currentCards[0].anchorId;
    for (const card of currentCards) {
      const el = document.getElementById(card.id);
      if (!el) continue;
      if (el.getBoundingClientRect().top - top <= 80) current = card.anchorId;
      else break;
    }
    if (container.scrollTop + container.clientHeight >= container.scrollHeight - 4) {
      current = currentCards[currentCards.length - 1].anchorId;
    }
    setActiveAnchor(prev => (prev === current ? prev : current));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '10px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.12)',
          overflow: 'hidden',
          height: '88vh',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          m: 0,
          px: 3,
          py: 0,
          height: 52,
          backgroundColor: C.bgWhite,
          borderBottom: `1px solid ${C.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: C.text }}>
          {isEditing ? `Роль: ${role?.name}` : 'Создать роль'}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: C.textLight, '&:hover': { color: C.text, backgroundColor: C.bg } }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </DialogTitle>

      {/* ── Content: боковое меню (разделы + якоря) и рабочая область ── */}
      <DialogContent sx={{ p: 0, backgroundColor: C.bg, overflow: 'hidden', flexGrow: 1, display: 'flex' }}>
        <Box
          sx={{
            width: 252,
            flexShrink: 0,
            backgroundColor: C.bgWhite,
            borderRight: `1px solid ${C.border}`,
            overflowY: 'auto'
          }}
        >
          {/* Раздел «Параметры» */}
          <NavSectionHeader label="Параметры" open={mainTab === 0} onClick={() => goTo(0)} />
          {mainTab === 0 && (
            <Box sx={{ py: 1, pl: 1, pr: 0.5 }}>
              {PARAM_ANCHORS.map(a => (
                <NavAnchor
                  key={a.id}
                  label={a.label}
                  active={activeAnchor === a.id}
                  onClick={() => goTo(0, undefined, a.id)}
                />
              ))}
            </Box>
          )}

          {/* Раздел «Матрица прав»: модули сгруппированы как в боковом меню панели */}
          <NavSectionHeader
            label="Матрица прав"
            open={mainTab === 1}
            badge={totalActivePerms}
            onClick={() => goTo(1)}
          />
          {mainTab === 1 && groupedModules.map(group => (
            <Box key={group.key}>
              <Typography
                sx={{
                  px: 1.5,
                  pt: 1.5,
                  pb: 0.5,
                  fontSize: '0.64rem',
                  fontWeight: 700,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: C.textLight
                }}
              >
                {group.title}
              </Typography>
              {group.entries.map(({ module: m, index: idx }) => {
                const activeCount = (m.permissions || []).filter(
                  p => permissions[p.code] && permissions[p.code] !== 'none' && permissions[p.code] !== 'false'
                ).length;
                const isSelected = idx === selectedModuleTab;
                const layout = isSelected ? currentLayout : null;
                const anchors = layout && layout.anchors.length > 1 ? layout.anchors : [];

                return (
                  <Box key={m.id}>
                    <Box
                      onClick={() => goTo(1, idx)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 1.5,
                        py: 1.1,
                        cursor: 'pointer',
                        borderLeft: `3px solid ${isSelected ? C.accent : 'transparent'}`,
                        backgroundColor: isSelected ? C.accentBg : 'transparent',
                        transition: 'all 0.12s',
                        '&:hover': { backgroundColor: isSelected ? C.accentBg : C.bg }
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.82rem',
                          fontWeight: isSelected ? 700 : 500,
                          color: isSelected ? C.accent : C.text,
                          lineHeight: 1.35
                        }}
                      >
                        {m.name}
                      </Typography>
                      {activeCount > 0 && (
                        <Box
                          sx={{
                            minWidth: 18,
                            height: 18,
                            borderRadius: '9px',
                            backgroundColor: isSelected ? C.accent : C.textLight,
                            color: '#fff',
                            fontSize: '0.62rem',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            px: 0.5,
                            flexShrink: 0,
                            ml: 0.5
                          }}
                        >
                          {activeCount}
                        </Box>
                      )}
                    </Box>
                    {anchors.length > 0 && (
                      <Box sx={{ py: 0.5, pl: 1.5, backgroundColor: C.accentBg + '80' }}>
                        {anchors.map(a => (
                          <NavAnchor
                            key={a.id}
                            label={a.label}
                            active={activeAnchor === a.id}
                            onClick={() => goTo(1, idx, a.id)}
                          />
                        ))}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>

        {/* Рабочая область: параметры или права выбранного модуля */}
        <Box ref={scrollRef} onScroll={handleContentScroll} sx={{ flex: 1, minWidth: 0, overflowY: 'auto' }}>
          {errorMessage && (
            <Alert
              severity="error"
              sx={{ mx: 3, mt: 2, borderRadius: 1.5, fontSize: '0.82rem' }}
              onClose={() => setErrorMessage(null)}
            >
              {errorMessage}
            </Alert>
          )}

          {/* TAB 1: PARAMETERS */}
          {mainTab === 0 && (
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  backgroundColor: C.bgWhite,
                  border: `1px solid ${C.border}`,
                  borderRadius: '8px',
                  overflow: 'hidden'
                }}
              >
                {/* Basic fields */}
                <Box id="rm-params-basic" sx={{ p: 2.5 }}>
                  <Section label="Основные данные">
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                      <TextField
                        label="Название роли"
                        placeholder="например, Старший модератор"
                        value={name}
                        onChange={handleNameChange}
                        size="small"
                        required
                        fullWidth
                        sx={textFieldSx}
                      />
                      <TextField
                        label="Идентификатор (slug)"
                        placeholder="senior_moderator"
                        value={slug}
                        onChange={(e) =>
                          setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))
                        }
                        size="small"
                        disabled={isEditing && role?.isSystem}
                        helperText={
                          isEditing && role?.isSystem ? 'Защищён - системная роль' : 'Латиница, цифры, _'
                        }
                        fullWidth
                        sx={textFieldSx}
                      />
                    </Box>
                    <TextField
                      label="Описание"
                      placeholder="Краткое описание функций и зон ответственности"
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      size="small"
                      multiline
                      rows={2}
                      fullWidth
                      sx={{ ...textFieldSx, mt: 2 }}
                    />
                  </Section>
                </Box>

                <Divider sx={{ borderColor: C.border }} />

                {/* Rank */}
                <Box id="rm-params-rank" sx={{ p: 2.5 }}>
                  <Section label="Иерархия доступа">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: C.text }}>
                          {hierarchyInfo.title}
                        </Typography>
                        <Typography sx={{ fontSize: '0.78rem', color: C.textMuted }}>
                          {hierarchyInfo.category}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.4,
                          borderRadius: '6px',
                          backgroundColor: hierarchyInfo.color + '18',
                          border: `1px solid ${hierarchyInfo.color}40`
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: '1.1rem',
                            color: hierarchyInfo.color,
                            fontVariantNumeric: 'tabular-nums'
                          }}
                        >
                          {level}
                        </Typography>
                      </Box>
                    </Box>

                    <Slider
                      value={level}
                      onChange={(_, val) => setLevel(Number(val))}
                      step={1}
                      min={10}
                      max={maxAllowedLevel}
                      marks={HIERARCHY_STEPS.filter(s => s.level <= maxAllowedLevel).map(s => ({
                        value: s.level,
                        label: `${s.level}`
                      }))}
                      sx={{
                        color: hierarchyInfo.color,
                        '& .MuiSlider-thumb': {
                          width: 18,
                          height: 18,
                          boxShadow: `0 0 0 3px ${hierarchyInfo.color}30`
                        },
                        '& .MuiSlider-markLabel': {
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: C.textMuted
                        },
                        '& .MuiSlider-mark': { display: 'none' },
                        '& .MuiSlider-track': { height: 4 },
                        '& .MuiSlider-rail': { height: 4 }
                      }}
                    />

                    {!operator.isSuperAdmin && (
                      <Typography sx={{ fontSize: '0.75rem', color: C.amber, mt: 1 }}>
                        Максимальный ранг для создания: {maxAllowedLevel}
                      </Typography>
                    )}
                  </Section>
                </Box>

                <Divider sx={{ borderColor: C.border }} />

                {/* Interface */}
                <Box id="rm-params-interface" sx={{ p: 2.5 }}>
                  <Section label="Целевой рабочий интерфейс">
                    <FormControl>
                      <RadioGroup
                        value={targetInterface}
                        onChange={(e) => handleInterfaceChange(e.target.value as TargetInterface)}
                        sx={{ gap: 1 }}
                      >
                        {[
                          {
                            value: 'admin',
                            label: 'Админ-панель (/admin)',
                            sub: 'Для офиса, руководства, модераторов, аудиторов и торговых отделов',
                            icon: <AdminIcon sx={{ fontSize: 18, color: C.accent }} />,
                            activeColor: C.accent,
                            activeBg: C.accentBg
                          },
                          {
                            value: 'dashboard',
                            label: 'Кабинет Аптеки (/dashboard)',
                            sub: 'Для точек продаж, кассиров, фармацевтов и складских помещений',
                            icon: <StorefrontIcon sx={{ fontSize: 18, color: C.green }} />,
                            activeColor: C.green,
                            activeBg: C.greenBg
                          }
                        ].map((opt) => {
                          const selected = targetInterface === opt.value;
                          return (
                            <FormControlLabel
                              key={opt.value}
                              value={opt.value}
                              control={
                                <Radio
                                  size="small"
                                  sx={{
                                    color: C.textLight,
                                    p: 1,
                                    '&.Mui-checked': { color: opt.activeColor }
                                  }}
                                />
                              }
                              label={
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    py: 0.75,
                                    px: 1.5,
                                    borderRadius: '6px',
                                    border: `1px solid ${selected ? opt.activeColor + '50' : C.border}`,
                                    backgroundColor: selected ? opt.activeBg : C.bgWhite,
                                    transition: 'all 0.15s',
                                    cursor: 'pointer',
                                    ml: 0
                                  }}
                                >
                                  {opt.icon}
                                  <Box>
                                    <Typography
                                      sx={{
                                        fontWeight: 700,
                                        fontSize: '0.82rem',
                                        color: selected ? opt.activeColor : C.text
                                      }}
                                    >
                                      {opt.label}
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.73rem', color: C.textMuted }}>
                                      {opt.sub}
                                    </Typography>
                                  </Box>
                                </Box>
                              }
                              sx={{ m: 0, alignItems: 'center' }}
                            />
                          );
                        })}
                      </RadioGroup>
                    </FormControl>
                  </Section>
                </Box>

                <Divider sx={{ borderColor: C.border }} />

                {/* Start page */}
                <Box id="rm-params-start" sx={{ p: 2.5 }}>
                  <Section label="Стартовая страница после входа">
                    <FormControl fullWidth size="small">
                      <Select
                        value={startPage}
                        onChange={(e) => setStartPage(String(e.target.value))}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
                        sx={{ fontSize: '0.85rem', borderRadius: '6px' }}
                      >
                        {startPageGroups.map(group => [
                          <ListSubheader
                            key={`h-${group.name}`}
                            sx={{ fontSize: '0.7rem', fontWeight: 700, lineHeight: '30px', color: C.textLight, textTransform: 'uppercase' }}
                          >
                            {group.name}
                          </ListSubheader>,
                          ...group.items.map(item => (
                            <MenuItem
                              key={item.key}
                              value={item.key}
                              sx={{ fontSize: '0.85rem' }}
                            >
                              {item.label}
                            </MenuItem>
                          ))
                        ])}
                      </Select>
                    </FormControl>
                    <Typography sx={{ fontSize: '0.73rem', color: C.textMuted, mt: 0.75 }}>
                      {`Список зависит от интерфейса: доступны только разделы ${targetInterface === 'admin' ? '/admin' : '/dashboard'}, права на которые включены в матрице. По умолчанию - профиль.`}
                      {' '}Если у сотрудника не заполнен профиль, при входе всегда откроется профиль.
                    </Typography>
                  </Section>
                </Box>

                <Divider sx={{ borderColor: C.border }} />

                {/* Badge */}
                <Box id="rm-params-badge" sx={{ p: 2.5 }}>
                  <BadgeColorPicker
                    badge={badge}
                    previewLabel={name || 'Название роли'}
                    onChange={setBadge}
                  />
                </Box>
              </Box>


            </Box>
          )}

          {/* Матрица прав: права выбранного модуля (якоря - карточки подразделов) */}
          {mainTab === 1 && (
            <Box sx={{ p: 2.5 }}>
              {currentModule && currentModule.permissions && currentModule.permissions.length > 0 ? (() => {
                const isCreateAccountModule = currentModule.id === 'create_account';

                // Мастер-переключатель модуля (Пункт №1 стандарта PBAC): <module>.menu,
                // для create_account - users.create. Подгруппы и якоря считает buildModuleLayout.
                const masterPerm = currentLayout!.masterPerm;
                const subgroups = currentLayout!.subgroups;

                const isMasterChecked = masterPerm
                  ? Boolean(permissions[masterPerm.code] && permissions[masterPerm.code] !== 'none' && permissions[masterPerm.code] !== 'false')
                  : true;

                const handleToggleAllInGroup = (groupPerms: ModulePermission[], enable: boolean) => {
                  if (isCreateAccountModule && !isMasterChecked) return;
                  groupPerms.forEach(p => {
                    if (RANK_100_ONLY_PERMISSIONS[p.code] && level < 100) return;
                    if (p.code.endsWith('.date_limit')) {
                      const restrictedVal = p.code.includes('monthly') ? 'last_month' : 'yesterday';
                      handleScopeChange(p.code, enable ? 'any' : restrictedVal);
                      return;
                    }
                    if (enable) {
                      let defScope = p.default_scope || 'true';
                      const scopesList = PERMISSION_SCOPES_CONFIG[p.code];
                      if (p.has_scope && scopesList && scopesList.length > 0) {
                        const first = scopesList.find(s => s.value !== 'none');
                        defScope = p.default_scope && p.default_scope !== 'none' ? p.default_scope : first?.value || 'all';
                      }
                      handleTogglePermission(p.code, true, defScope);
                    } else {
                      handleTogglePermission(p.code, false, p.has_scope ? 'none' : 'false');
                    }
                  });
                };

                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {/* 1. Главный пункт раздела / Мастер-переключатель (Пункт №1 стандарта) */}
                    {masterPerm && (
                      <Box
                        id={currentLayout!.masterCardId}
                        sx={{
                          p: 2.25,
                          mb: 2.5,
                          borderRadius: '12px',
                          border: `1.5px solid ${isMasterChecked ? '#93c5fd' : '#e2e8f0'}`,
                          background: isMasterChecked
                            ? 'linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)'
                            : '#f8fafc',
                          boxShadow: isMasterChecked
                            ? '0 2px 8px -2px rgba(37, 99, 235, 0.12)'
                            : '0 1px 3px 0 rgba(0, 0, 0, 0.02)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          transition: 'all 0.15s ease'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, minWidth: 0, flex: 1 }}>
                          <Box
                            sx={{
                              width: 42,
                              height: 42,
                              borderRadius: '10px',
                              backgroundColor: isMasterChecked ? '#2563eb' : '#e2e8f0',
                              color: isMasterChecked ? '#ffffff' : '#64748b',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                              boxShadow: isMasterChecked ? '0 2px 6px rgba(37, 99, 235, 0.3)' : 'none',
                              transition: 'all 0.15s ease'
                            }}
                          >
                            {isCreateAccountModule ? (
                              <PersonAddIcon sx={{ fontSize: 22 }} />
                            ) : (
                              <LayersIcon sx={{ fontSize: 22 }} />
                            )}
                          </Box>

                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.3 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: isMasterChecked ? '#0f172a' : '#64748b' }}>
                                {masterPerm.name}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.68rem',
                                  color: C.textLight,
                                  fontFamily: 'monospace',
                                  backgroundColor: C.bg,
                                  px: 0.75,
                                  py: 0.1,
                                  borderRadius: '4px',
                                  border: `1px solid ${C.border}`
                                }}
                              >
                                {masterPerm.code}
                              </Typography>
                            </Box>

                            <Typography sx={{ fontSize: '0.76rem', color: '#64748b', lineHeight: 1.35 }}>
                              {isCreateAccountModule
                                ? 'Управляет отображением кнопки «+ Добавить пользователя» и возможностью создания учетных записей внутри раздела «Пользователи»'
                                : (masterPerm.description || `Отображение раздела «${currentModule.name}» в боковом меню`)}
                            </Typography>
                          </Box>
                        </Box>

                        <Tooltip title={isMasterChecked ? 'Выключить' : 'Включить'}>
                          <Switch
                            checked={isMasterChecked}
                            size="medium"
                            onChange={(e) => {
                              handleTogglePermission(
                                masterPerm.code,
                                e.target.checked,
                                e.target.checked ? 'true' : (isCreateAccountModule ? 'none' : 'false')
                              );
                            }}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#2563eb' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2563eb' }
                            }}
                          />
                        </Tooltip>
                      </Box>
                    )}

                    {/* Информационное уведомление для create_account если выключен мастер-тумблер */}
                    {isCreateAccountModule && !isMasterChecked && (
                      <Box
                        sx={{
                          p: 1.75,
                          mb: 2.5,
                          borderRadius: '10px',
                          backgroundColor: '#f8fafc',
                          border: '1px dashed #cbd5e1',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5
                        }}
                      >
                        <InfoIcon sx={{ color: '#64748b', fontSize: 20, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.78rem', color: '#475569' }}>
                          Создание пользователей отключено переключателем пункта №1 выше. Чтобы настроить разрешенные роли для назначения, сначала включите «Создание пользователей».
                        </Typography>
                      </Box>
                    )}

                    {/* 2. Объединенные карточки подразделов */}
                    {subgroups.map((group) => {
                      const totalCount = group.permissions.length;
                      const activeCount = group.permissions.filter(p => {
                        const val = permissions[p.code];
                        return val && val !== 'none' && val !== 'false';
                      }).length;
                      const isAllActive = activeCount === totalCount;
                      const isNoneActive = activeCount === 0;
                      const isParentDisabled = isCreateAccountModule && !isMasterChecked;

                      return (
                        <Box
                          key={group.name}
                          id={group.cardId}
                          sx={{
                            mb: 2.5,
                            borderRadius: '12px',
                            border: '1px solid #e2e8f0',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 1px 3px 0 rgba(15, 23, 42, 0.04), 0 1px 2px -1px rgba(15, 23, 42, 0.02)',
                            overflow: 'hidden',
                            opacity: isParentDisabled ? 0.65 : 1,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor: isParentDisabled ? '#e2e8f0' : '#cbd5e1',
                              boxShadow: isParentDisabled ? 'none' : '0 4px 12px 0 rgba(15, 23, 42, 0.05)'
                            }
                          }}
                        >
                          {/* Шапка карточки подраздела */}
                          <Box
                            sx={{
                              px: 2.25,
                              py: 1.25,
                              background: 'linear-gradient(180deg, #fbfcfe 0%, #f8fafc 100%)',
                              borderBottom: '1px solid #e2e8f0',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1.5,
                              flexWrap: 'wrap'
                            }}
                          >
                            {/* Слева: иконка группы, название группы, количество */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                              <Box
                                sx={{
                                  width: 28,
                                  height: 28,
                                  borderRadius: '7px',
                                  backgroundColor: isAllActive ? '#ecfdf5' : activeCount > 0 ? '#eff6ff' : '#f1f5f9',
                                  color: isAllActive ? '#059669' : activeCount > 0 ? '#2563eb' : '#94a3b8',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0
                                }}
                              >
                                <FolderIcon sx={{ fontSize: 16 }} />
                              </Box>
                              <Typography
                                sx={{
                                  fontSize: '0.86rem',
                                  fontWeight: 750,
                                  color: '#0f172a',
                                  letterSpacing: '-0.01em'
                                }}
                              >
                                {group.name}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '0.7rem',
                                  fontWeight: 600,
                                  color: '#64748b',
                                  backgroundColor: '#ffffff',
                                  border: '1px solid #e2e8f0',
                                  px: 0.9,
                                  py: 0.2,
                                  borderRadius: '12px'
                                }}
                              >
                                {totalCount} {totalCount === 1 ? 'настройка' : totalCount < 5 ? 'настройки' : 'настроек'}
                              </Typography>
                            </Box>

                            {/* Справа: бейдж активности и аккуратные быстрые действия */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box
                                sx={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.75,
                                  px: 1.1,
                                  py: 0.35,
                                  borderRadius: '20px',
                                  fontSize: '0.72rem',
                                  fontWeight: 650,
                                  backgroundColor: isAllActive ? '#ecfdf5' : activeCount > 0 ? '#eff6ff' : '#f8fafc',
                                  color: isAllActive ? '#065f46' : activeCount > 0 ? '#1e40af' : '#64748b',
                                  border: `1px solid ${isAllActive ? '#a7f3d0' : activeCount > 0 ? '#bfdbfe' : '#e2e8f0'}`
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    backgroundColor: isAllActive ? '#10b981' : activeCount > 0 ? '#3b82f6' : '#94a3b8'
                                  }}
                                />
                                {isAllActive
                                  ? `Все активны (${activeCount}/${totalCount})`
                                  : activeCount > 0
                                    ? `Активно: ${activeCount} из ${totalCount}`
                                    : `Отключено (0/${totalCount})`}
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Button
                                  size="small"
                                  disabled={isAllActive || isParentDisabled}
                                  onClick={() => handleToggleAllInGroup(group.permissions, true)}
                                  startIcon={<CheckIcon sx={{ fontSize: '13px !important' }} />}
                                  sx={{
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    py: 0.3,
                                    px: 1,
                                    borderRadius: '6px',
                                    color: (isAllActive || isParentDisabled) ? '#cbd5e1' : '#2563eb',
                                    backgroundColor: 'transparent',
                                    '&:hover': {
                                      backgroundColor: '#eff6ff',
                                      color: '#1d4ed8'
                                    },
                                    '&.Mui-disabled': {
                                      color: '#cbd5e1'
                                    }
                                  }}
                                >
                                  Вкл все
                                </Button>
                                <Button
                                  size="small"
                                  disabled={isNoneActive || isParentDisabled}
                                  onClick={() => handleToggleAllInGroup(group.permissions, false)}
                                  startIcon={<ClearIcon sx={{ fontSize: '13px !important' }} />}
                                  sx={{
                                    fontSize: '0.72rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    py: 0.3,
                                    px: 1,
                                    borderRadius: '6px',
                                    color: (isNoneActive || isParentDisabled) ? '#cbd5e1' : '#dc2626',
                                    backgroundColor: 'transparent',
                                    '&:hover': {
                                      backgroundColor: '#fef2f2',
                                      color: '#b91c1c'
                                    },
                                    '&.Mui-disabled': {
                                      color: '#cbd5e1'
                                    }
                                  }}
                                >
                                  Выкл все
                                </Button>
                              </Box>
                            </Box>
                          </Box>

                          {/* Список прав внутри карточки */}
                          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {group.permissions.map((perm, rowIdx) => {
                              const currentScope = permissions[perm.code] || (perm.has_scope ? 'none' : 'false');
                              const scopesList = PERMISSION_SCOPES_CONFIG[perm.code];

                              const rank100Hint = RANK_100_ONLY_PERMISSIONS[perm.code];
                              const isLockedByRank = Boolean(rank100Hint) && level < 100;
                              const isDateLimitPerm = perm.code.endsWith('.date_limit');
                              let parentPermCode: string | null = null;
                              if (isDateLimitPerm) {
                                parentPermCode = perm.code.replace('.date_limit', '');
                              } else if (perm.code.endsWith('.protect_copy')) {
                                const baseCode = perm.code.replace('.protect_copy', '');
                                // У доставки и мат. отчёта родитель - право ".view", а не префикс кода
                                parentPermCode = (baseCode === 'reports.delivery' || baseCode === 'reports.math')
                                  ? `${baseCode}.view`
                                  : baseCode;
                              } else if (perm.code.startsWith('reports.view_summary.')) {
                                parentPermCode = 'reports.view_summary';
                              } else if (perm.code.startsWith('reports.view_director.')) {
                                parentPermCode = 'reports.view_director';
                              } else if (perm.code.startsWith('reports.history.') && perm.code !== 'reports.history.view') {
                                // Аптеки и подробности работают только при включённой кнопке «История»
                                parentPermCode = 'reports.history.view';
                              } else if (perm.code.startsWith('reports.delivery.') && perm.code !== 'reports.delivery.view') {
                                parentPermCode = 'reports.delivery.view';
                              } else if (perm.code.startsWith('reports.math.report.') && perm.code !== 'reports.math.report.view') {
                                parentPermCode = 'reports.math.report.view';
                              } else if (perm.code.startsWith('reports.math.changes.') && perm.code !== 'reports.math.changes.view') {
                                parentPermCode = 'reports.math.changes.view';
                              } else if (perm.code.startsWith('reports.math.fields.') && perm.code !== 'reports.math.fields.view') {
                                parentPermCode = 'reports.math.fields.view';
                              } else if (perm.code.startsWith('reports.math.') && perm.code !== 'reports.math.view') {
                                // report.view / changes.view / fields.view зависят от входа в раздел
                                parentPermCode = 'reports.math.view';
                              } else if (perm.code === 'documents.invoices.warnings_actions' || perm.code === 'documents.invoices.warnings_export') {
                                // Действия и выгрузка работают только во включённой вкладке «Проблемные документы» (а она - внутри зоны просмотра)
                                const isOff = (code: string) => !permissions[code] || permissions[code] === 'none' || permissions[code] === 'false';
                                parentPermCode = isOff('documents.invoices.view') ? 'documents.invoices.view' : 'documents.invoices.warnings';
                              } else if (perm.code === 'documents.marking.export') {
                                // Выгрузка кодов работает только внутри включённого раздела «КМ'ы»
                                parentPermCode = 'documents.marking.view';
                              } else if (perm.code === 'timesheet.open') {
                                // Переход в табель работает только для аптек из зоны вывода данных модуля «Табель РБ»
                                parentPermCode = 'timesheet.data';
                              } else if (perm.code.startsWith('tables.revenue.entry.') && perm.code !== 'tables.revenue.entry.view') {
                                // Изменение окна ввода и исключений работает только при включенной вкладке «Окно ввода»
                                parentPermCode = 'tables.revenue.entry.view';
                              } else if (perm.code.startsWith('timesheet.') && perm.code !== 'timesheet.menu') {
                                // Данные и настройки работают только при включённом пункте меню «Табель РБ»
                                parentPermCode = 'timesheet.menu';
                              } else if (perm.code === 'guide.remains.sums') {
                                // Сумма без цены раскрыла бы цену, поэтому работает только вместе с ценами (а они - внутри зоны просмотра)
                                const isOff = (code: string) => !permissions[code] || permissions[code] === 'none' || permissions[code] === 'false';
                                parentPermCode = isOff('guide.remains.view') ? 'guide.remains.view' : 'guide.remains.prices';
                              } else if (perm.code.startsWith('guide.remains.') && perm.code !== 'guide.remains.view') {
                                // Цены, телефон, данные КМ, выгрузка и обновление работают только внутри зоны просмотра остатков
                                parentPermCode = 'guide.remains.view';
                              } else if (perm.code.startsWith('guide.nomenclature.') && perm.code !== 'guide.nomenclature.view') {
                                // Выгрузка и сверка с источниками работают только при входе в «Справочник»
                                parentPermCode = 'guide.nomenclature.view';
                              } else if (perm.code.startsWith('documents.invoices.analytics_')) {
                                // Подвкладка аналитики считается в пересечении зон просмотра документов и аналитики
                                const isOff = (code: string) => !permissions[code] || permissions[code] === 'none' || permissions[code] === 'false';
                                parentPermCode = isOff('documents.invoices.view') ? 'documents.invoices.view' : 'documents.invoices.analytics';
                              } else if (DOCUMENTS_VIEW_DEPENDENTS.includes(perm.code)) {
                                // Суммы, повторная обработка, проблемные документы и настройки работают только внутри зоны просмотра
                                parentPermCode = 'documents.invoices.view';
                              } else if (AUDIT_VIEW_DEPENDENTS.includes(perm.code)) {
                                // Карточка записи, выгрузка и очистка работают только внутри журнала
                                parentPermCode = 'audit.view';
                              } else if (JOURNAL_VIEW_DEPENDENTS.includes(perm.code)) {
                                // Аналитика и выгрузка строятся только по записям, видимым в журнале
                                parentPermCode = 'journal.view';
                              }
                              const isParentPermDisabled = Boolean(
                                parentPermCode &&
                                interfaceCodes.has(parentPermCode) &&
                                (!permissions[parentPermCode] || permissions[parentPermCode] === 'none' || permissions[parentPermCode] === 'false')
                              );
                              const isRowDisabled = isLockedByRank || isParentDisabled || isParentPermDisabled;
                              const isLastRow = rowIdx === group.permissions.length - 1;

                              let isChecked = Boolean(
                                permissions[perm.code] &&
                                permissions[perm.code] !== 'none' &&
                                permissions[perm.code] !== 'false'
                              );
                              if (isDateLimitPerm) {
                                isChecked = permissions[perm.code] === 'any';
                              }

                              // Specific roles scope support
                              const isSpecificRolesPerm = perm.code === 'users.create_role_scope';
                              const isSpecificScope = isSpecificRolesScope(currentScope);
                              const selectValue = isSpecificScope
                                ? 'specific_roles'
                                : (isDateLimitPerm ? (permissions[perm.code] || 'any') : currentScope);
                              const selectedRolesList = parseSpecificRoles(currentScope);

                              return (
                                <Box
                                  key={perm.code}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    backgroundColor: (isChecked || isDateLimitPerm) && !isParentPermDisabled ? '#ffffff' : '#fafafa',
                                    borderBottom: isLastRow ? 'none' : '1px solid #f1f5f9',
                                    borderLeft: `3px solid ${(isChecked || isDateLimitPerm) && !isParentPermDisabled ? '#2563eb' : 'transparent'}`,
                                    opacity: isRowDisabled ? 0.55 : 1,
                                    transition: 'all 0.12s ease',
                                    '&:hover': {
                                      backgroundColor: (isChecked || isDateLimitPerm) && !isParentPermDisabled ? '#f8fafc' : '#f4f6f8'
                                    }
                                  }}
                                >
                                  {/* Основная строка разрешения */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'space-between',
                                      gap: 2,
                                      px: 2.25,
                                      py: 1.35
                                    }}
                                  >
                                    {/* Слева: название + код + бейджи + описание */}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                        <Typography
                                          sx={{
                                            fontWeight: (isChecked || isDateLimitPerm) && !isParentPermDisabled ? 650 : 500,
                                            fontSize: '0.84rem',
                                            color: (isChecked || isDateLimitPerm) && !isParentPermDisabled ? '#0f172a' : '#64748b'
                                          }}
                                        >
                                          {perm.name}
                                        </Typography>
                                        <Typography
                                          sx={{
                                            fontSize: '0.68rem',
                                            color: C.textLight,
                                            fontFamily: 'monospace',
                                            backgroundColor: C.bg,
                                            px: 0.75,
                                            py: 0.1,
                                            borderRadius: '4px',
                                            border: `1px solid ${C.border}`
                                          }}
                                        >
                                          {perm.code}
                                        </Typography>
                                        {isLockedByRank && (
                                          <Tooltip title={rank100Hint}>
                                            <Chip
                                              icon={<LockIcon sx={{ fontSize: '11px !important' }} />}
                                              label="Только ранг 100"
                                              size="small"
                                              sx={{
                                                height: 18,
                                                fontSize: '0.62rem',
                                                backgroundColor: C.dangerBg,
                                                color: C.danger,
                                                fontWeight: 700,
                                                border: `1px solid ${C.danger}30`
                                              }}
                                            />
                                          </Tooltip>
                                        )}
                                      </Box>
                                      {perm.description && (
                                        <Typography sx={{ fontSize: '0.74rem', color: '#64748b', mt: 0.25, lineHeight: 1.3 }}>
                                          {perm.description}
                                        </Typography>
                                      )}
                                    </Box>

                                    {/* Справа: селектор скоупа + переключатель-тумблер */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
                                      {!isLockedByRank && !isParentDisabled && !isParentPermDisabled && !!perm.has_scope && scopesList && scopesList.length > 0 && (
                                        <Select
                                          value={selectValue}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            if (isDateLimitPerm) {
                                              handleScopeChange(perm.code, val);
                                            } else if (val === 'specific_roles') {
                                              const current = parseSpecificRoles(permissions[perm.code]);
                                              if (current.length === 0) {
                                                const defaultSlugs = (availableRoles || [])
                                                  .filter(r => level >= 100 || r.level < level)
                                                  .map(r => r.slug.toLowerCase());
                                                handleScopeChange(
                                                  perm.code,
                                                  defaultSlugs.length > 0 ? `specific_roles:${defaultSlugs.join(',')}` : 'specific_roles'
                                                );
                                              } else {
                                                handleScopeChange(perm.code, `specific_roles:${current.join(',')}`);
                                              }
                                            } else {
                                              handleScopeChange(perm.code, val);
                                            }
                                          }}
                                          disabled={isRowDisabled || (!isChecked && !isDateLimitPerm)}
                                          size="small"
                                          renderValue={(val) => {
                                            const sc = scopesList.find(s => s.value === val);
                                            if (!sc) return null;
                                            return (
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                <Box sx={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: sc.badgeColor || C.textLight, flexShrink: 0 }} />
                                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 650, color: (isChecked || isDateLimitPerm) && !isParentPermDisabled ? C.text : C.textMuted }}>
                                                  {sc.label} {isSpecificScope && selectedRolesList.length > 0 ? `(${selectedRolesList.length})` : ''}
                                                </Typography>
                                              </Box>
                                            );
                                          }}
                                          sx={{
                                            minWidth: 190,
                                            maxWidth: 220,
                                            fontSize: '0.8rem',
                                            fontWeight: 600,
                                            backgroundColor: (isChecked || isDateLimitPerm) && !isParentPermDisabled ? C.bgWhite : C.bg,
                                            borderRadius: '6px',
                                            color: (isChecked || isDateLimitPerm) && !isParentPermDisabled ? C.text : C.textMuted,
                                            '& .MuiSelect-select': { py: '6px' },
                                            '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
                                            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.textLight },
                                            '&.Mui-disabled': { opacity: 0.5 }
                                          }}
                                        >
                                          {scopesList.map(sc => (
                                            <MenuItem key={sc.value} value={sc.value} sx={{ py: 0.75 }}>
                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box
                                                  sx={{
                                                    width: 7,
                                                    height: 7,
                                                    borderRadius: '50%',
                                                    backgroundColor: sc.badgeColor || C.textLight,
                                                    flexShrink: 0
                                                  }}
                                                />
                                                <Box>
                                                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.2 }}>
                                                    {sc.label}
                                                  </Typography>
                                                  {sc.description && (
                                                    <Typography sx={{ fontSize: '0.68rem', color: C.textMuted, lineHeight: 1.2 }}>
                                                      {sc.description}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </Box>
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      )}

                                      {/* Тумблер переключатель Switch */}
                                      {isRowDisabled ? (
                                        <Tooltip
                                          title={
                                            isParentDisabled
                                              ? 'Создание пользователей отключено в пункте №1 выше'
                                              : isParentPermDisabled
                                                ? 'Сначала включите родительский подраздел'
                                                : 'Действие заблокировано'
                                          }
                                        >
                                          <span>
                                            <Switch checked={false} disabled size="small" />
                                          </span>
                                        </Tooltip>
                                      ) : (
                                        <Tooltip
                                          title={
                                            isDateLimitPerm
                                              ? (isChecked ? 'Ограничить последним днем/месяцем' : 'Снять ограничение (любое число)')
                                              : (isChecked ? 'Выключить' : 'Включить')
                                          }
                                        >
                                          <Switch
                                            checked={isChecked}
                                            size="small"
                                            onChange={(e) => {
                                              const nextChecked = e.target.checked;
                                              if (isDateLimitPerm) {
                                                const restrictedVal = perm.code.includes('monthly') ? 'last_month' : 'yesterday';
                                                handleScopeChange(perm.code, nextChecked ? 'any' : restrictedVal);
                                              } else {
                                                if (!nextChecked) {
                                                  handleTogglePermission(perm.code, false, perm.has_scope ? 'none' : 'false');
                                                } else {
                                                  let defScope = perm.default_scope || 'true';
                                                  if (perm.has_scope && scopesList && scopesList.length > 0) {
                                                    const first = scopesList.find(s => s.value !== 'none');
                                                    defScope =
                                                      perm.default_scope && perm.default_scope !== 'none'
                                                        ? perm.default_scope
                                                        : first?.value || 'all';
                                                  }
                                                  handleTogglePermission(perm.code, true, defScope);
                                                }
                                              }
                                            }}
                                            sx={{
                                              '& .MuiSwitch-switchBase.Mui-checked': { color: C.accent },
                                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                backgroundColor: C.accent
                                              }
                                            }}
                                          />
                                        </Tooltip>
                                      )}
                                    </Box>
                                  </Box>

                                  {/* Дополнительная панель назначения конкретных ролей */}
                                  {isChecked && !isParentDisabled && isSpecificRolesPerm && isSpecificScope && (
                                    <Box
                                      sx={{
                                        px: 2,
                                        pb: 2,
                                        pt: 1.25,
                                        borderTop: `1px dashed ${C.border}`,
                                        backgroundColor: '#fafbfc'
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'space-between',
                                          flexWrap: 'wrap',
                                          gap: 1,
                                          mb: 1.5
                                        }}
                                      >
                                        <Box>
                                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: C.text }}>
                                            Разрешенные роли для назначения
                                          </Typography>
                                          <Typography sx={{ fontSize: '0.72rem', color: C.textMuted }}>
                                            Отметьте роли, которые сможет выбирать оператор при регистрации новых пользователей
                                          </Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => {
                                              const allowedSlugs = availableRoles
                                                .filter(r => level >= 100 || r.level < level)
                                                .map(r => r.slug.toLowerCase());
                                              handleScopeChange(
                                                perm.code,
                                                allowedSlugs.length > 0 ? `specific_roles:${allowedSlugs.join(',')}` : 'specific_roles'
                                              );
                                            }}
                                            sx={{
                                              fontSize: '0.72rem',
                                              py: 0.25,
                                              px: 1,
                                              textTransform: 'none',
                                              borderColor: C.border,
                                              color: C.accent,
                                              '&:hover': { borderColor: C.accent, backgroundColor: C.accentBg }
                                            }}
                                          >
                                            Выбрать все доступные ({availableRoles.filter(r => level >= 100 || r.level < level).length})
                                          </Button>
                                          <Button
                                            size="small"
                                            onClick={() => handleScopeChange(perm.code, 'specific_roles')}
                                            sx={{
                                              fontSize: '0.72rem',
                                              py: 0.25,
                                              px: 1,
                                              textTransform: 'none',
                                              color: C.textMuted,
                                              '&:hover': { color: C.danger }
                                            }}
                                          >
                                            Снять выбор
                                          </Button>
                                        </Box>
                                      </Box>

                                      <Box
                                        sx={{
                                          display: 'grid',
                                          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                                          gap: 1
                                        }}
                                      >
                                        {availableRoles
                                          .slice()
                                          .sort((a, b) => b.level - a.level)
                                          .map(r => {
                                            const rSlug = r.slug.toLowerCase();
                                            const isRoleSelected = selectedRolesList.includes(rSlug);
                                            const isHigherOrEqual = !operator.isSuperAdmin && r.level >= level;

                                            return (
                                              <Box
                                                key={r.slug}
                                                onClick={() => {
                                                  if (isHigherOrEqual) return;
                                                  const next = isRoleSelected
                                                    ? selectedRolesList.filter(s => s !== rSlug)
                                                    : [...selectedRolesList, rSlug];
                                                  handleScopeChange(
                                                    perm.code,
                                                    next.length > 0 ? `specific_roles:${next.join(',')}` : 'specific_roles'
                                                  );
                                                }}
                                                sx={{
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  gap: 1,
                                                  p: 1,
                                                  borderRadius: '6px',
                                                  border: `1.5px solid ${isRoleSelected
                                                    ? r.badge?.text || C.accent
                                                    : C.border
                                                    }`,
                                                  backgroundColor: isRoleSelected
                                                    ? r.badge?.bg || C.accentBg
                                                    : isHigherOrEqual
                                                      ? '#f1f5f9'
                                                      : C.bgWhite,
                                                  cursor: isHigherOrEqual ? 'not-allowed' : 'pointer',
                                                  opacity: isHigherOrEqual ? 0.5 : 1,
                                                  transition: 'all 0.12s',
                                                  '&:hover': !isHigherOrEqual
                                                    ? {
                                                      borderColor: r.badge?.text || C.accent,
                                                      boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
                                                    }
                                                    : {}
                                                }}
                                              >
                                                <Checkbox
                                                  checked={isRoleSelected}
                                                  disabled={isHigherOrEqual}
                                                  size="small"
                                                  sx={{
                                                    p: 0.25,
                                                    color: C.textLight,
                                                    '&.Mui-checked': { color: r.badge?.text || C.accent }
                                                  }}
                                                />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                                    <Typography
                                                      sx={{
                                                        fontSize: '0.8rem',
                                                        fontWeight: 700,
                                                        color: isRoleSelected ? (r.badge?.text || C.text) : C.text,
                                                        lineHeight: 1.2
                                                      }}
                                                    >
                                                      {r.name}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: '0.68rem', color: C.textMuted, fontFamily: 'monospace' }}>
                                                      {r.slug}
                                                    </Typography>
                                                  </Box>
                                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.3 }}>
                                                    <Chip
                                                      label={`Ранг ${r.level}`}
                                                      size="small"
                                                      sx={{
                                                        height: 16,
                                                        fontSize: '0.6rem',
                                                        fontWeight: 700,
                                                        backgroundColor: '#ffffff',
                                                        color: r.badge?.text || C.textMuted,
                                                        border: `1px solid ${C.border}`
                                                      }}
                                                    />
                                                    <Typography sx={{ fontSize: '0.65rem', color: C.textLight }}>
                                                      {r.targetInterface === 'admin' ? 'Офис (/admin)' : 'Аптека (/dashboard)'}
                                                    </Typography>
                                                  </Box>
                                                </Box>
                                                {isHigherOrEqual && (
                                                  <Tooltip title={`Ранг ${r.level} >= ранга роли (${level}). Нельзя назначить по субординации.`}>
                                                    <LockIcon sx={{ fontSize: 15, color: C.textLight, mr: 0.5 }} />
                                                  </Tooltip>
                                                )}
                                              </Box>
                                            );
                                          })}
                                      </Box>

                                      <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                        <Typography sx={{ fontSize: '0.72rem', color: selectedRolesList.length > 0 ? C.green : C.danger, fontWeight: 600 }}>
                                          {selectedRolesList.length > 0
                                            ? `Выбрано ролей для назначения: ${selectedRolesList.length}`
                                            : '⚠️ Внимание: ни одна роль не выбрана! Создание пользователей будет заблокировано.'}
                                        </Typography>
                                      </Box>
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                );
              })() : (
                <Box sx={{ py: 6, textAlign: 'center' }}>
                  <InfoIcon sx={{ fontSize: 32, color: C.textLight, mb: 1 }} />
                  <Typography sx={{ fontSize: '0.85rem', color: C.textMuted }}>
                    В данном модуле нет настраиваемых прав
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </DialogContent>

      {/* ── Footer ── */}
      <DialogActions
        sx={{
          px: 3,
          py: 1.75,
          backgroundColor: C.bgWhite,
          borderTop: `1px solid ${C.border}`,
          justifyContent: 'space-between',
          flexShrink: 0
        }}
      >
        <Typography sx={{ fontSize: '0.75rem', color: C.textLight }}>
          {totalActivePerms > 0
            ? `Настроено прав: ${totalActivePerms}`
            : 'Изменения вступят в силу сразу после сохранения'}
        </Typography>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            sx={{
              color: C.textMuted,
              fontWeight: 600,
              fontSize: '0.82rem',
              textTransform: 'none',
              '&:hover': { color: C.text }
            }}
          >
            Закрыть
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveClick}
            disabled={isSubmitting}
            disableElevation
            sx={primaryBtnSx}
          >
            {isSubmitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : isEditing ? (
              'Сохранить'
            ) : (
              'Создать роль'
            )}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
